// Les 30 templates du handoff design (Numéros virtuels SMS).
// Chaque thème = un accent + un mode (clair/sombre) + une identité de dashboard
// (fam = famille de layout, pal = surcharges de palette, tok = surcharges de tokens).
// Référence: design_handoff_sms_virtuels (Design Tokens).

// ---------------------------------------------------------------------------
// Registre de polices. syne/manrope/outfit/jetbrains sont déjà chargées
// globalement (index.html / index.css) -> g:null (rien à injecter).
export const FONTS = {
  syne: { css: "'Syne', sans-serif", g: null },
  manrope: { css: "'Manrope', sans-serif", g: null },
  outfit: { css: "'Outfit', sans-serif", g: null },
  jetbrains: { css: "'JetBrains Mono', monospace", g: null },
  spacemono: { css: "'Space Mono', monospace", g: "Space+Mono:wght@400;700" },
  fraunces: { css: "'Fraunces', serif", g: "Fraunces:opsz,wght@9..144,500;9..144,700;9..144,900" },
  archivo: { css: "'Archivo Black', sans-serif", g: "Archivo+Black" },
  vt323: { css: "'VT323', monospace", g: "VT323" },
};

// Injecte (une seule fois) la feuille Google Fonts d'une police du registre.
export const ensureGoogleFont = (fontKey) => {
  try {
    const font = FONTS[fontKey];
    if (!font || !font.g) return;
    if (typeof document === "undefined") return;
    const id = `gf-${fontKey}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${font.g}&display=swap`;
    document.head.appendChild(link);
  } catch {
    /* noop — l'échec d'injection ne doit jamais casser le rendu */
  }
};

export const THEMES = [
  { key: "clean-trust", label: "Clean Trust", accent: "#1f6feb", mode: "light", fam: "sidebar" },
  {
    key: "aqua-fresh", label: "Aqua Fresh", accent: "#06b6a4", mode: "light", fam: "float",
    pal: { page: "#ecfbf8", border: "#d8efe9", field: "#f0faf7" },
    tok: { font: "outfit", radius: { card: 20, control: 12 }, borderW: 0, shadowCard: "0 14px 40px rgba(6,182,164,.10)", button: "pill" },
  },
  {
    key: "aurora-glass", label: "Aurora Glass", accent: "#a855f7", mode: "dark", fam: "neon",
    pal: { page: "#0c0a1a", card: "rgba(255,255,255,.06)", ink: "#f1eaff", sub: "#9a8fc0", field: "rgba(255,255,255,.08)", border: "rgba(255,255,255,.13)", glass: true },
    tok: { radius: { card: 18, control: 12 }, shadowCard: "0 20px 60px rgba(168,85,247,.25)", cardBorder: "rgba(255,255,255,.14)", decor: "aurora" },
  },
  {
    key: "bauhaus", label: "Bauhaus", accent: "#1356b0", mode: "light", fam: "topbar",
    pal: { page: "#f4f1ea", border: "#111111", field: "#ebe7dd", sub: "#4c483f" },
    tok: { radius: { card: 0, control: 0, chip: 0 }, borderW: 2, accent2: "#f4c20d", navActive: "block", button: "square" },
  },
  {
    key: "bold-editorial", label: "Bold Editorial", accent: "#e4572e", mode: "light", fam: "editorial",
    pal: { page: "#f4ede1", card: "#faf5ea", ink: "#1a1714", sub: "#6b6156", border: "#1a1714", field: "#efe7d6", hairline: "#d8cdb8" },
    tok: { displayFont: "fraunces", labelFont: "spacemono", radius: { card: 0, control: 0, chip: 99 }, borderW: 2, navActive: "underline", button: "pill", btnBg: "ink" },
  },
  {
    key: "brutalist", label: "Brutalist", accent: "#caa800", mode: "light", fam: "topbar",
    pal: { page: "#f5f4ef", border: "#111111", field: "#eeede6", sub: "#44403a" },
    tok: {
      labelFont: "spacemono", radius: { card: 0, control: 0, chip: 0 }, borderW: 3,
      shadowCard: "4px 4px 0 #111", accent2: "#ffe600", navActive: "block", navBlockBg: "ink",
      button: "hard", btnBg: "ink", btnShadow: "4px 4px 0 #ffe600", onAccent: "#111111",
    },
  },
  {
    key: "claymorphism", label: "Claymorphism", accent: "#6c5ce7", mode: "light", fam: "float",
    pal: { page: "#ecebff", ink: "#2c2950", sub: "#6f6b9a", border: "#dcd9f5", field: "#f3f2ff" },
    tok: { font: "outfit", radius: { card: 22, control: 14 }, borderW: 0, shadowCard: "8px 8px 20px #c9c5f0, -8px -8px 20px #ffffff" },
  },
  {
    key: "corporate-blue", label: "Corporate Blue", accent: "#1d4ed8", mode: "light", fam: "sidebar",
    pal: { page: "#f3f6fb", border: "#dde3ee" },
    tok: { font: "manrope", radius: { card: 10, control: 8 }, shadowCard: "0 1px 2px rgba(15,23,42,.05)", header: "solid" },
  },
  {
    key: "coupon", label: "Coupon", accent: "#e63946", mode: "light", fam: "topbar",
    pal: { page: "#fff7e6", border: "rgba(230,57,70,.4)", field: "#fffdf5", sub: "#8a7a5f", hairline: "rgba(230,57,70,.25)" },
    tok: { radius: { card: 14, control: 10 }, borderW: 2, borderStyle: "dashed", navActive: "block" },
  },
  {
    key: "dark-minimal", label: "Dark Minimal", accent: "#e3e3e3", mode: "dark", fam: "sidebar",
    pal: { page: "#0c0c0d", card: "#131316", ink: "#e8e8ea", sub: "#8a8a93", border: "#222226", field: "#1a1a1d" },
    tok: { font: "manrope", radius: { card: 8, control: 8 }, onAccent: "#111111" },
  },
  {
    key: "dark-tech", label: "Dark Tech", accent: "#00e5a0", mode: "dark", fam: "terminal",
    pal: { page: "#07090d", card: "#0d1210", ink: "#d7efe6", sub: "#6d8a80", border: "#1e2a26", field: "#10171a" },
    tok: { font: "jetbrains", radius: { card: 6, control: 4, chip: 6 }, onAccent: "#06231a", labelCase: "upper", labelSpacing: ".08em" },
  },
  {
    key: "gaming-glow", label: "Gaming Glow", accent: "#7cff00", mode: "dark", fam: "neon",
    pal: { page: "#08090f", card: "#0e1118", ink: "#e8f5dc", sub: "#7f8a75", border: "#1e2430", field: "#12161f" },
    tok: { font: "outfit", radius: { card: 14, control: 10 }, shadowCard: "0 0 24px rgba(124,255,0,.25)", cardBorder: "rgba(124,255,0,.2)", decor: "grid", onAccent: "#08090f" },
  },
  {
    key: "glass-deep-blue", label: "Glass Deep Blue", accent: "#38bdf8", mode: "dark", fam: "neon",
    pal: { page: "linear-gradient(160deg,#040a1a,#0a1730)", card: "rgba(255,255,255,.06)", ink: "#eaf2ff", sub: "#8ea3c4", field: "rgba(255,255,255,.08)", border: "rgba(255,255,255,.14)", glass: true },
    tok: { radius: { card: 18, control: 12 }, shadowCard: "0 24px 60px rgba(0,0,0,.4)", cardBorder: "rgba(255,255,255,.16)" },
  },
  {
    key: "gradient-mesh", label: "Gradient Mesh", accent: "#7c5cff", mode: "light", fam: "float",
    pal: { page: "#fdfbff", border: "#eee6ff", field: "#f7f3ff" },
    tok: { radius: { card: 20, control: 12 }, shadowCard: "0 16px 44px rgba(124,92,255,.12)", decor: "mesh", accent2: "#4f9cff", button: "gradient" },
  },
  {
    key: "lavender-calm", label: "Lavender Calm", accent: "#8b7cd6", mode: "light", fam: "sidebar",
    pal: { page: "#f6f4fb", border: "#e7e2f5" },
    tok: { radius: { card: 16, control: 12 }, shadowCard: "0 10px 30px rgba(139,124,214,.08)" },
  },
  {
    key: "magazine", label: "Magazine", accent: "#b03a2e", mode: "light", fam: "editorial",
    pal: { page: "#f6f3ec", card: "#fffdf7", ink: "#22201c", sub: "#6e675c", border: "#22201c", field: "#f0ece1", hairline: "#ddd6c6" },
    tok: { displayFont: "fraunces", radius: { card: 0, control: 0, chip: 99 }, navActive: "underline" },
  },
  {
    key: "midnight-luxe", label: "Midnight Luxe", accent: "#c9a14a", mode: "dark", fam: "sidebar",
    pal: { page: "#0b0f0d", card: "#101614", ink: "#ece7da", sub: "#98937f", border: "rgba(201,161,74,.27)", field: "#16211c" },
    tok: {
      labelCase: "upper", labelSpacing: ".14em", radius: { card: 4, control: 10 },
      shadowCard: "0 30px 80px rgba(0,0,0,.5)", button: "outline", buttonRadius: 999, onAccent: "#0b0f0d",
    },
  },
  {
    key: "mobile-app", label: "Mobile App", accent: "#4f46e5", mode: "light", fam: "float",
    pal: { page: "#eef0fb", border: "#e4e7f6", field: "#f3f4fc" },
    tok: { font: "outfit", radius: { card: 24, control: 16 }, borderW: 0, shadowCard: "0 12px 34px rgba(79,70,229,.12)", button: "pill" },
  },
  {
    key: "mono-noir-blanc", label: "Mono Noir Blanc", accent: "#111111", mode: "light", fam: "editorial",
    pal: { page: "#ffffff", card: "#ffffff", ink: "#111111", sub: "#555555", border: "#111111", field: "#f5f5f5", hairline: "#e2e2e2" },
    tok: { radius: { card: 0, control: 0, chip: 0 }, onAccent: "#ffffff", button: "square" },
  },
  {
    key: "neo-memphis", label: "Neo Memphis", accent: "#ff5d8f", mode: "light", fam: "topbar",
    pal: { page: "#fdf2e4", border: "#111111", field: "#fdf3e6", sub: "#6b5f56" },
    tok: { radius: { card: 12, control: 10 }, borderW: 2, shadowCard: "5px 5px 0 #2bd9c7", accent2: "#2bd9c7" },
  },
  {
    key: "neon-cyber", label: "Neon Cyber", accent: "#ff2bd6", mode: "dark", fam: "neon",
    pal: { page: "#07060f", card: "#0d0a1c", ink: "#f4ecff", sub: "#8f86ad", border: "#2a2344", field: "#120e24" },
    tok: {
      labelFont: "spacemono", radius: { card: 12, control: 10 }, shadowCard: "0 0 18px rgba(255,43,214,.35)",
      cardBorder: "rgba(255,43,214,.55)", decor: "grid", accent2: "#2bf0ff", button: "gradient", onAccent: "#0b0616",
    },
  },
  {
    key: "pastel-soft", label: "Pastel Soft", accent: "#2bb673", mode: "light", fam: "float",
    pal: { page: "#fff5ee", border: "#f0e0d3", field: "#fdf1e7" },
    tok: { font: "outfit", radius: { card: 20, control: 12 }, borderW: 0, shadowCard: "0 10px 30px rgba(43,182,115,.10)", button: "pill" },
  },
  {
    key: "red-energy", label: "Red Energy", accent: "#e11d2a", mode: "light", fam: "topbar",
    pal: { page: "#ffffff", border: "#eeeeee", field: "#f7f7f7" },
    tok: { radius: { card: 12, control: 10 }, navActive: "block", button: "hard", btnShadow: "0 6px 0 #b00d18" },
  },
  {
    key: "sunset", label: "Sunset", accent: "#ff5e62", mode: "light", fam: "float",
    pal: { page: "#fff5f0", border: "#ffe0d5", field: "#fff0e9" },
    tok: { radius: { card: 18, control: 12 }, borderW: 0, shadowCard: "0 14px 40px rgba(255,94,98,.14)", accent2: "#ff9966", button: "gradient" },
  },
  {
    key: "swiss-dark", label: "Swiss Dark", accent: "#ff4d00", mode: "dark", fam: "topbar",
    pal: { page: "#101012", card: "#17171a", sub: "#9a9aa2", border: "#2a2a2e", field: "#1d1d21" },
    tok: { labelCase: "upper", labelSpacing: ".08em", radius: { card: 0, control: 0, chip: 0 }, navActive: "underline" },
  },
  {
    key: "swiss-minimal", label: "Swiss Minimal", accent: "#e2231a", mode: "light", fam: "editorial",
    pal: { page: "#ffffff", card: "#ffffff", ink: "#111111", sub: "#666666", border: "#111111", field: "#f4f4f4", hairline: "#e5e5e5" },
    tok: { labelCase: "upper", labelSpacing: ".08em", radius: { card: 0, control: 0, chip: 0 }, navActive: "underline" },
  },
  {
    key: "type-poster", label: "Type Poster", accent: "#ff3b00", mode: "light", fam: "editorial",
    pal: { page: "#f2f0eb", card: "#ffffff", ink: "#111111", sub: "#55534d", border: "#111111", field: "#eae8e1", hairline: "#dcdad2" },
    tok: { displayFont: "archivo", radius: { card: 0, control: 0, chip: 0 }, borderW: 2, navActive: "underline" },
  },
  {
    key: "vintage-terminal", label: "Vintage Terminal", accent: "#22c55e", mode: "dark", fam: "terminal",
    pal: { page: "#080c07", card: "#0a120b", ink: "#d9ffe3", sub: "#6fae7f", border: "#1c4a26", field: "#0d1a0f" },
    tok: { font: "vt323", labelFont: "vt323", radius: { card: 6, control: 4, chip: 6 }, decor: "scanline", onAccent: "#06120a" },
  },
  {
    key: "warm-earth", label: "Warm Earth", accent: "#c1612d", mode: "light", fam: "sidebar",
    pal: { page: "#f3ece0", card: "#fffdf8", ink: "#2f2418", sub: "#7a6a54", border: "#e2d5c3", field: "#f8f2e7" },
    tok: { radius: { card: 14, control: 10 }, shadowCard: "0 8px 24px rgba(120,80,40,.08)" },
  },
  {
    key: "y2k-chrome", label: "Y2K Chrome", accent: "#2563eb", mode: "light", fam: "float",
    pal: { page: "#dfe6ef", border: "#ffffff", field: "#edf2f8", sub: "#5a6b80", hairline: "#d9e2ec" },
    tok: {
      radius: { card: 20, control: 12 }, shadowCard: "inset 0 1px 1px rgba(255,255,255,.6), 0 8px 22px rgba(37,99,235,.25)",
      accent2: "#5ea3ff", button: "gradient", buttonRadius: 999,
    },
  },
];

// Compat anciens templates -> Clean Trust
const ALIASES = { classic: "clean-trust", modern: "gradient-mesh", minimal: "swiss-minimal" };

export const DEFAULT_TEMPLATE = "clean-trust";

export const getTheme = (key) => {
  const resolved = ALIASES[key] || key;
  return THEMES.find((t) => t.key === resolved) || THEMES[0];
};

// Palette dérivée du mode (tokens du handoff).
export const getPalette = (theme) => {
  const accent = theme.accent;
  if (theme.mode === "dark") {
    return {
      mode: "dark", accent,
      soft: `${accent}29`,
      page: "#0a0a0e", card: "#15151c", ink: "#eceef2", sub: "#8a8a98",
      border: "#262630", field: "#1c1c24",
    };
  }
  return {
    mode: "light", accent,
    soft: `${accent}1f`,
    page: "#f3f5fb", card: "#ffffff", ink: "#181820", sub: "#6c6c80",
    border: "#e9e9f1", field: "#f7f8fc",
  };
};

// ---------------------------------------------------------------------------
// Tokens de dashboard. BASE = Clean Trust actuel (zéro régression).
const BASE = {
  layout: "sidebar",
  font: "syne", displayFont: null, labelFont: null,
  radius: { card: 18, control: 12, chip: 99 },
  borderW: 1, borderStyle: "solid",
  shadowCard: "none",
  header: "glass", navActive: "soft", navBlockBg: "accent",
  button: "solid", buttonRadius: null, btnBg: "accent", btnShadow: null,
  labelCase: "none", labelSpacing: null,
  onAccent: "#ffffff", accent2: null, decor: null, cardBorder: null,
};

// Familles = deltas partiels appliqués au-dessus de BASE selon le layout.
const FAMILIES = {
  sidebar: {},
  topbar: { layout: "topbar" },
  float: { layout: "float" },
  editorial: { layout: "editorial", navActive: "underline" },
  terminal: { layout: "terminal", header: "prompt" },
  neon: { layout: "neon", navActive: "glow" },
};

// Style complet du dashboard pour un template donné.
// Merge : BASE <- FAMILIES[fam] <- palette du mode <- pal du thème <- tok du thème.
export const getShopStyle = (key) => {
  const theme = getTheme(key);
  const basePal = getPalette(theme); // couleurs par défaut du mode + soft
  const fam = FAMILIES[theme.fam] || FAMILIES.sidebar;
  const pal = theme.pal || {};
  const tok = theme.tok || {};
  const t = {
    ...BASE, ...fam, ...tok,
    radius: { ...BASE.radius, ...(fam.radius || {}), ...(tok.radius || {}) },
  };
  const colors = {
    page: basePal.page, card: basePal.card, ink: basePal.ink,
    sub: basePal.sub, border: basePal.border, field: basePal.field,
    ...pal,
  };
  return {
    mode: theme.mode,
    accent: theme.accent,
    accent2: t.accent2,
    soft: basePal.soft,
    onAccent: t.onAccent,
    page: colors.page, card: colors.card, ink: colors.ink, sub: colors.sub,
    border: colors.border, field: colors.field,
    hairline: colors.hairline || colors.border, // séparateurs fins (lignes de tableaux…)
    glass: !!colors.glass,
    fontCss: (FONTS[t.font] || FONTS.syne).css,
    displayFontCss: t.displayFont ? (FONTS[t.displayFont] || FONTS.syne).css : null,
    labelFontCss: t.labelFont ? (FONTS[t.labelFont] || FONTS.syne).css : null,
    fontKeys: [...new Set([t.font, t.displayFont, t.labelFont].filter(Boolean))],
    layout: t.layout,
    radius: t.radius,
    borderW: t.borderW,
    borderStyle: t.borderStyle,
    shadowCard: t.shadowCard,
    cardBorder: t.cardBorder,
    header: t.header,
    navActive: t.navActive,
    navBlockBg: t.navBlockBg,
    button: t.button,
    buttonRadius: t.buttonRadius,
    btnBg: t.btnBg,
    btnShadow: t.btnShadow,
    labelCase: t.labelCase,
    labelSpacing: t.labelSpacing,
    decor: t.decor,
  };
};
